import { useState, useEffect } from 'react';
import { useAuth } from './context.es.js';
import { getObjFromQueryString, inIframe, isValidPhoneFormat } from '@teko/utils';
import { setIBankingChannel } from './ibanking.es.js';

var LoginPermission;
(function (LoginPermission) {
    LoginPermission["FACEBOOK"] = "social:facebook:login";
    LoginPermission["GOOGLE"] = "social:google:login";
    LoginPermission["PHONE_NUMBER"] = "passwordless:login";
})(LoginPermission || (LoginPermission = {}));
const checkAutoOpenPhoneNumberLogin = (loginPermissionMap) => {
    const { facebook, google, phoneNumber } = loginPermissionMap;
    return !(facebook || google) && phoneNumber;
};
const useLogin = ({ challenge, partner, router, }) => {
    const { loginWithPartner, user, authService, userService } = useAuth();
    const [loading, setLoading] = useState(true);
    const [phone, setPhone] = useState('');
    const [noti, setNoti] = useState({
        show: false,
        message: '',
    });
    const [isSentOTP, setIsSentOTP] = useState(false);
    const [showPhoneLogin, setShowPhoneLogin] = useState(false);
    const [canEditPhone, setCanEditPhone] = useState(true);
    const [loginPermissions, setLoginPermissions] = useState({
        facebook: false,
        google: false,
        phoneNumber: false,
    });
    const [reCaptchaConfig, setReCaptchaConfig] = useState({
        enabled: false,
        site_key: '',
    });
    useEffect(() => {
        const cpRefCode = sessionStorage.getItem('ref_code');
        if (cpRefCode) {
            userService
                .getRefCodeInfo(cpRefCode)
                .then((res) => {
                var _a;
                setCanEditPhone(false);
                setPhone(((_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.telephone) || '');
            })
                .catch((e) => {
                console.error('Get ref code error', e);
            });
        }
    }, []);
    useEffect(() => {
        const handlePostMessageLogin = (event) => {
            if (typeof event.data === 'object' &&
                event.data.data &&
                event.data.signature) {
                loginWithPartner(event.data);
            }
        };
        if (!partner) {
            if (!challenge || user) {
                router === null || router === void 0 ? void 0 : router.push('/');
                return;
            }
            setLoading(true);
            authService
                .requestLogin(challenge)
                .then((response) => {
                const permissions = response.client.permissions;
                const newPermissions = {
                    facebook: permissions.includes(LoginPermission.FACEBOOK),
                    google: permissions.includes(LoginPermission.GOOGLE),
                    phoneNumber: permissions.includes(LoginPermission.PHONE_NUMBER),
                };
                setLoginPermissions(newPermissions);
                setReCaptchaConfig(response.client.recaptcha_configuration);
                setShowPhoneLogin(checkAutoOpenPhoneNumberLogin(newPermissions));
                if (response.skip && response.redirect_to) {
                    window.location.replace(response.redirect_to);
                }
            })
                .catch((err) => {
                console.error(err);
                router === null || router === void 0 ? void 0 : router.push('/');
            })
                .finally(() => {
                setLoading(false);
            });
        }
        else {
            const queries = getObjFromQueryString(window.location.search);
            if (Object.keys(queries).length) {
                setIBankingChannel(queries.channel);
                loginWithPartner(queries);
            }
            else {
                if (inIframe()) {
                    setIBankingChannel('any');
                }
                window.addEventListener('message', handlePostMessageLogin, false);
            }
        }
        return () => {
            window.removeEventListener('message', handlePostMessageLogin);
        };
    }, [challenge]);
    const sendOTP = async (message, recaptchaToken) => {
        var _a;
        setLoading(true);
        try {
            const res = await authService.sendOTP(challenge || '', phone, recaptchaToken);
            if (res.success) {
                setNoti({ show: true, message });
                setIsSentOTP(true);
            }
            else
                setNoti({
                    show: true,
                    message: 'Đã có lỗi xảy ra, vui lòng thử lại',
                });
        }
        catch (error) {
            console.error(error);
            if (((_a = error === null || error === void 0 ? void 0 : error.error) === null || _a === void 0 ? void 0 : _a.code) === 1800 || (error === null || error === void 0 ? void 0 : error.code) === 1800) {
                setNoti({
                    show: true,
                    message: 'Bạn đã đăng nhập quá số lần cho phép trong ngày',
                });
            }
            else {
                setNoti({
                    show: true,
                    message: 'Đã có lỗi xảy ra, vui lòng thử lại',
                });
            }
        }
        finally {
            setLoading(false);
        }
    };
    const handleOTPChange = (otp) => {
        setLoading(true);
        authService
            .loginIAMWithPhone(challenge || '', phone, otp)
            .then((res) => {
            if (res.redirect_to)
                window.location.href = res.redirect_to;
            else {
                setLoading(false);
                setNoti({ show: true, message: 'Mã OTP không chính xác' });
            }
        })
            .catch(() => {
            setLoading(false);
            setNoti({ show: true, message: 'Mã OTP không chính xác' });
        });
    };
    const handlePhoneChange = (number) => {
        setPhone(number);
        setNoti(isValidPhoneFormat(number)
            ? { show: false, message: '' }
            : { show: true, message: 'Vui lòng nhập đúng định dạng số điện thoại' });
    };
    const handleSendOTP = (message, recaptchaToken) => {
        if (!phone) {
            setNoti({
                show: true,
                message: 'Vui lòng nhập số điện thoại',
            });
            return;
        }
        if (noti.show)
            return;
        sendOTP(message, recaptchaToken);
    };
    const handleResendOTP = (message, recaptchaToken) => {
        sendOTP(message, recaptchaToken);
    };
    const handleIamResponse = (data) => {
        if (data.error) {
            throw new Error(data.error);
        }
        if (data.redirect_to) {
            window.location.assign(data.redirect_to);
        }
    };
    const loginWithPhone = () => {
        setShowPhoneLogin((prev) => {
            setNoti((noti) => (Object.assign(Object.assign({}, noti), { show: false })));
            return !prev;
        });
    };
    const loginWithFacebook = async (authResponse) => {
        try {
            if (!authResponse) {
                throw new Error('Invalid response from Facebook');
            }
            const { data } = await authService.loginSocialIAM(challenge || '', authResponse.accessToken, 'facebook');
            handleIamResponse(data);
        }
        catch (e) {
            console.error(e.message);
            alert('Đăng nhập với Facebook thất bại!');
        }
    };
    const loginWithGoogle = async (gapiInstance) => {
        try {
            const googleUser = await gapiInstance.signIn({
                prompt: 'select_account',
            });
            const { data } = await authService.loginSocialIAM(challenge || '', googleUser.getAuthResponse(true).access_token, 'google');
            handleIamResponse(data);
        }
        catch (e) {
            console.error(e);
            alert('Đăng nhập với Google thất bại!');
        }
    };
    return {
        loading,
        loginWithFacebook,
        loginWithGoogle,
        loginWithPhone,
        handleOTPChange,
        handleSendOTP,
        handleResendOTP,
        handlePhoneChange,
        phone,
        noti,
        isSentOTP,
        allowLoginWithFacebook: loginPermissions.facebook,
        allowLoginWithGoogle: loginPermissions.google,
        allowLoginWithPhone: loginPermissions.phoneNumber,
        showPhoneLogin,
        canEditPhone,
        reCaptchaConfig,
    };
};

export { useLogin };
